import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import api, { CATEGORY_LABELS, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (r.data.variants && r.data.variants.length) setVariant(r.data.variants[0]);
    });
  }, [slug]);

  if (!product) {
    return <div className="px-6 py-32 text-center text-[#7C897B]">Loading…</div>;
  }

  const hasVariants = product.variants && product.variants.length > 0;
  const unitPrice = hasVariants ? variant?.price : product.sale_price || product.price;

  const add = () => {
    addItem(product, hasVariants ? variant.label : "", unitPrice, qty);
    toast.success(`${product.name} added to your basket`);
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto py-10 md:py-16">
      <Link to="/shop" className="inline-flex items-center gap-2 text-[#7C897B] hover:text-[#B85C38] text-sm uppercase tracking-widest mb-8" data-testid="back-to-shop">
        <ArrowLeft className="w-4 h-4" /> Back to Shop
      </Link>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div className="aspect-square bg-[#F0EBE1] rounded-sm overflow-hidden border border-[#E6DFD3]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
        </div>

        <div>
          <p className="kicker">{CATEGORY_LABELS[product.category] || product.category}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2D372B] mt-2 tracking-tight">{product.name}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-sans text-2xl text-[#B85C38]" data-testid="product-price">{formatPrice(unitPrice)}</span>
            {!hasVariants && product.sale_price && (
              <span className="text-lg text-[#7C897B] line-through">{formatPrice(product.price)}</span>
            )}
          </div>

          <p className="text-[#4A5D4E] leading-relaxed mt-6 text-lg">{product.description}</p>

          {hasVariants && (
            <div className="mt-8">
              <p className="kicker text-[#7C897B]">{product.variant_label || "Options"}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {product.variants.map((v) => (
                  <button
                    key={v.label}
                    data-testid={`variant-${v.label}`}
                    onClick={() => setVariant(v)}
                    className={`px-4 py-2.5 rounded-sm border text-sm transition-colors ${
                      variant?.label === v.label
                        ? "border-[#B85C38] bg-[#B85C38]/10 text-[#B85C38]"
                        : "border-[#E6DFD3] text-[#4A5D4E] hover:border-[#B85C38]"
                    }`}
                  >
                    {v.label} · {formatPrice(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#E6DFD3] rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 hover:bg-[#F0EBE1]" data-testid="qty-decrease">−</button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 py-3 hover:bg-[#F0EBE1]" data-testid="qty-increase">+</button>
            </div>
            <button
              data-testid="add-to-cart-button"
              onClick={add}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B85C38] hover:bg-[#9A4A2B] text-white uppercase tracking-widest text-sm py-3.5 transition-colors rounded-sm"
            >
              <Check className="w-4 h-4" /> Add to Basket
            </button>
          </div>

          <p className="text-xs text-[#7C897B] mt-6 leading-relaxed">
            Hand made in small batches. Free shipping on orders over $75.
          </p>
        </div>
      </div>
    </div>
  );
}
